<template>
  <v-app>
    <v-progress-linear
      v-if="visibleLoading"
      indeterminate
      background-color="#FF4800"
      color="#FFFFFF"
    ></v-progress-linear>
    <confirmation-modal-notifications />
    <loading v-if="visibleCustomLoading" />
    <custom-snackbar />
    <v-overlay :value="visibleOverlay" color="#FFF"></v-overlay>

    <template v-if="visibleOnboardingIntro && !visibleLoading">
      <onboardingIntro @eventCloseIntro="closeIntro" />
    </template>
    <template v-else>
      <onboarding v-if="visibleOnboarding" />
      <product-list :visible.sync="visibleProducts" />
      <menu-list :visible.sync="visibleMenuList" />
      <custom-notifications />
      <custom-sidebar
        v-if="showSidebar && oidcUser.name"
        :showTooltip="{
          tooltipProductList: visibleProducts,
          tooltipMenuList: visibleMenuList,
        }"
      />
      <collapsible-menu />
      <background-fade v-show="displayMenu"></background-fade>
    </template>
    <wrapper-transition>
      <template #content>
        <div :class="isHome ? 'container-fluid px-5' : 'container px-0'">
          <custom-breadcrumb v-if="showBreadcrumb && !visibleOnboardingIntro" />
          <transition name="fade" mode="out-in">
            <router-view />
          </transition>
        </div>
      </template>
    </wrapper-transition>
    <footer-transition v-if="isHome && !visibleOnboardingIntro" />
  </v-app>
</template>

<script>
import Vue from "vue";
import { mapActions, mapGetters, mapMutations, mapState } from "vuex";
import { userInfo } from "@/common/services/modules/auth";
import { getUserDetails } from "@/common/services";
import { tagManager } from "../src/common/utils/tagManager.js";

export default Vue.extend({
  name: "App",
  components: {
    loading: () => import("@/common/components/loading/Loading.vue"),
    "confirmation-modal-notifications": () =>
      import("@/views/notifications/confirmation-modal/ConfirmationModal.vue"),
    "wrapper-transition": () =>
      import("@/common/components/wrapper-transition/WrapperTransition.vue"),
    "custom-sidebar": () =>
      import("@/common/components/design-system/sidebar/Sidebar.vue"),
    "collapsible-menu": () =>
      import(
        "@/common/components/design-system/collapsible-menu/CollapsibleMenu.vue"
      ),
    "custom-snackbar": () =>
      import("@/common/components/design-system/snackbar/Snackbar.vue"),
    "custom-breadcrumb": () =>
      import("@/common/components/design-system/breadcrumbs/Breadcrumbs.vue"),
    onboarding: () => import("@/views/onboarding/Onboarding.vue"),
    onboardingIntro: () => import("@/views/onboarding/OnboardingIntro.vue"),
    "custom-notifications": () =>
      import(
        "@/common/components/custom-components/notifications/Notifications.vue"
      ),
    "product-list": () =>
      import(
        "@/common/components/custom-components/product-list/ProductList.vue"
      ),
    "menu-list": () =>
      import("@/common/components/custom-components/menu-list/MenuList.vue"),
    "footer-transition": () =>
      import("@/common/components/design-system/footer/Footer.vue"),
    "background-fade": () =>
      import(
        "./common/components/simple-components/background-fade/BackgroundFade.vue"
      ),
  },
  beforeMount() {
    this.setVisibleOnboardingIntro(
      JSON.parse(localStorage.getItem("firstAccessLocal") || true)
    );
  },
  async created() {
    const hasCode = localStorage.getItem("code");
    if (hasCode) {
      const { data } = await userInfo();
      this.$store.commit("UPDATE_USER_INFOS", data);

      const userId = this.$store.state?.userInfos?.sub;

      if (userId) {
        const {
          data,
          data: { entities },
        } = await getUserDetails({ userId });

        this.$store.commit("ADD_USER_DATA", data);
        this.$store.commit("SET_LIST_ENTITIES", entities);
      }
    }
  },
  data() {
    return {
      showSidebar: true,
      showBreadcrumb: true,
      timer: 0,
      displayMenu: false,
    };
  },
  computed: {
    ...mapState("onboarding", ["visibleOnboarding", "visibleOnboardingIntro"]),
    ...mapState([
      "visibleMenu",
      "visibleLoading",
      "visibleOverlay",
      "visibleCustomLoading",
      "visibleProducts",
      "visibleMenuList",
      "visibleNotifications",
      "listEntities",
    ]),
    ...mapGetters(["oidcUser"]),
    ...mapGetters("entitiesDetails", ["getEntitiesQuantity"]),
    ...mapState(["userData", "currentEntity"]),
    isHome() {
      return this.$route.name === "Home";
    },
    currentRouteName() {
      return this.$route.name;
    },
    visibleAnyMenu() {
      return (
        this.visibleMenuList ||
        this.visibleNotifications ||
        this.visibleProducts ||
        this.visibleMenu
      );
    },
    isProd() {
      return window.location.href.includes("my.clearsale");
    },
    isQA() {
      return window.location.href.includes("myqa.clearsale");
    },
  },
  watch: {
    currentRouteName(value) {
      const routeIsTerm = value === "terms";
      this.showSidebar = !routeIsTerm;
      this.showBreadcrumb = !routeIsTerm;
    },
    visibleAnyMenu(value) {
      clearTimeout(this.timer);
      if (!value) {
        this.timer = setTimeout(() => {
          this.displayMenu = false;
        }, 130);
      } else {
        this.displayMenu = true;
      }
    },
    userData(value) {
      if (Object.keys(value).length > 0) {
        this.luckyOrange(this.userData);
        this.track(this.userData);
        this.googleTagManager(this.userData);
      }
    },
  },
  methods: {
    ...mapActions(["createProducts"]),
    ...mapActions("entitiesDetails", ["updateEntityQuantity"]),
    ...mapMutations("onboarding", {
      setVisibleOnboardingIntro: "SET_VISIBLE_ONBOARDING_INTRO",
    }),
    ...mapMutations({
      addUserData: "ADD_USER_DATA",
      updateCurrentEntity: "SET_USER_CURRENT_ENTITY",
    }),

    closeIntro() {
      this.setVisibleOnboardingIntro(false);
    },
    luckyOrange(user) {
      const isProd = window.location.href.includes("my.clearsale");

      const dominionClearSale =
        user.userEmail.includes("@clear.sale") ||
        user.userEmail.includes("@clearsale.com.br");

      if (isProd && !dominionClearSale) {
        const script = document.createElement("script");
        script.setAttribute(
          "src",
          "https://tools.luckyorange.com/core/lo.js?site-id=9b259b96"
        );
        script.setAttribute("async", "");
        script.setAttribute("defer", "");
        document.head.appendChild(script);
      }
    },
    googleTagManager(user) {
      const isProd = window.location.href.includes("my.clearsale");

      const dominionClearSale =
        user.userEmail.includes("@clear.sale") ||
        user.userEmail.includes("@clearsale.com.br");

      if (isProd && !dominionClearSale) {
        tagManager(window, document, "script", "dataLayer", "GTM-K8HDH8HH");

        const scriptGoogle2 = document.createElement("noscript");
        const iframe = document.createElement("iframe");
        iframe.setAttribute(
          "src",
          "https://www.googletagmanager.com/ns.html?id=GTM-K8HDH8HH"
        );
        iframe.setAttribute("height", "0");
        iframe.setAttribute("width", "0");
        iframe.setAttribute("style", "display:none;visibility:hidden");
        scriptGoogle2.appendChild(iframe);

        document.body.append(scriptGoogle2);
      }
    },
    track(user) {
      const dominionClearSale =
        user.userEmail.includes("@clear.sale") ||
        user.userEmail.includes("@clearsale.com.br") ||
        user.userEmail.includes("@uah.com.br");

      if ((this.isProd || this.isQA) && !dominionClearSale) {
        (function (f, b, g) {
          const a = {
            key: "YevNddKU",
            customer: {
              name: user.userName,
              email: user.userEmail,
              phone: user.userCellPhone,
            },
          };
          var e;
          const c = f.getElementsByTagName(b)[0];
          if (f.getElementById(g)) {
            return;
          }
          e = f.createElement(b);
          e.id = g;
          e.src = "https://app.track.co/widget.min.js";
          e.type = "text/javascript";
          e.async = true;
          e.onload = e.onreadystatechange = function () {
            const h = this.readyState;
            if (h && h !== "complete" && h !== "loaded") {
              return;
            }
            try {
              const c = new TrackWidget();
              c.createWidget(a);
            } catch (i) {}
          };
          c.parentNode.insertBefore(e, c);
        })(document, "script", "trackwidget-js");
      }
    },
  },
});
</script>

<style lang="scss">
.theme--light.v-application {
  background: rgba(239, 239, 239, 1);
}
</style>
